import { RuntimeVariableInfo } from "./RuntimeVariableInfo";
import { RuntimeVariableValue } from "./RuntimeVariableValue";
import { EnvironmentComparisonBadge } from "./EnvironmentComparisonBadge";
import type { EnvironmentComparisonRow as ComparisonRow } from "./environmentComparison";

interface EnvironmentComparisonRowProps {
  row: ComparisonRow;
}

export function EnvironmentComparisonRow({
  row,
}: EnvironmentComparisonRowProps) {
  const needsAttention =
    ["different", "only-stg", "only-staging"].includes(row.status) ||
    row.protectedMetadataDiffers;

  return (
    <article
      className={
        needsAttention
          ? "rounded-lg border border-amber-500/30 bg-amber-500/5 p-3"
          : "rounded-lg border bg-background/60 p-3"
      }
      data-comparison-status={row.status}
    >
      <div className="grid min-w-0 gap-3 lg:grid-cols-[minmax(14rem,0.8fr)_minmax(0,1fr)_minmax(0,1fr)] lg:items-start">
        <div className="min-w-0 space-y-2">
          <div className="flex min-w-0 items-start justify-between gap-2 lg:block">
            <div className="flex min-w-0 items-start gap-1">
              <h3 className="min-w-0 whitespace-normal break-words font-mono text-xs font-medium [overflow-wrap:anywhere]">
                {row.name}
                {row.container ? (
                  <span className="ml-1 text-muted-foreground">
                    ({row.container})
                  </span>
                ) : null}
              </h3>
              <RuntimeVariableInfo name={row.name} />
            </div>
            <div className="shrink-0 lg:mt-2">
              <EnvironmentComparisonBadge row={row} />
            </div>
          </div>
        </div>

        <EnvironmentValue
          label="Production"
          environment="stg"
          entry={row.stg}
        />
        <EnvironmentValue
          label="Engineering staging"
          environment="staging"
          entry={row.staging}
        />
      </div>
    </article>
  );
}

interface EnvironmentValueProps {
  label: string;
  environment: "stg" | "staging";
  entry: ComparisonRow["stg"];
}

function EnvironmentValue({
  label,
  environment,
  entry,
}: EnvironmentValueProps) {
  return (
    <div className="min-w-0 rounded-md bg-muted/45 px-3 py-2">
      <div className="mb-1 text-[11px] font-medium text-muted-foreground">
        {label} <span className="font-mono">({environment})</span>
      </div>
      <div className="min-w-0 whitespace-normal">
        <RuntimeVariableValue entry={entry} wrap />
      </div>
    </div>
  );
}
